// An item's trips on a month calendar: pick which trips to show, each drawn as
// a colored bar across the days it covers, labelled with where it starts and
// ends. This renders BELOW the trips list, on the list's own page and URL -- a
// second reading of the same data rather than a sibling page. The list answers
// "what trips exist"; the grid answers "when am I away, and does this new one
// collide with the last", and having them one scroll apart is the point.
//
// It does its OWN read, and has to. The list above pages through
// tripsByItemId ASC for its table, so at any moment it holds a prefix of the
// legs; a bar spans a whole trip, so a partial read would draw the trips it did
// find too short with nothing to say so, and -- worse -- the handover below
// swaps in STORED legs out of this array, which would silently come up short.
// Hence loadAllTripsFresh: every leg, and it WAITS first. The commonest way to
// arrive is straight off the trip detail page, where typing Days kicks off a
// sibling re-date cascade that bypasses the save queue entirely, so the loader
// waits out both the lanes and the cascade chain before the dates are trusted.
//
// Read-only. Nothing here writes, so there is no autosave, no dirty guard, and
// no handover snapshot to distrust.
//
// Both view knobs live in the query string -- now the LIST page's query string:
//
//   ?month=YYYY-MM   the displayed month; absent means the month of the
//                    soonest trip. Never written back when it's the default --
//                    pushing a month the user didn't choose would put a history
//                    entry between them and the page they came from.
//   ?trips=a,b,c     the picked trips, each key encoded on its own (a group key
//                    can contain a comma -- 'n:seattle, wa'). ABSENT means "the
//                    default", i.e. whatever is still running; present and
//                    empty means the user really did uncheck everything.
//
// Below the grid, the same selection on a map: one line per leg, colored by the
// TRIP so a line and its bar are the same color by construction (they read one
// colorIndexFor, not two tables). It follows the LEGEND, not the grid -- paging
// months doesn't repaint it -- because "where do these trips go" is not a
// question about April. No weather: a selection routinely spans months of past
// and future, so a forecast would be noise even where the bounds didn't blow
// past useMapWeather's oversize gate.
//
// The arithmetic -- the six-week window, a trip's day span, lane packing, the
// week-by-week segmentation -- is all in fleet-shared/TripCalendar.mjs and is
// unit-tested there. This file is loading, state, and navigation.

import { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { useSearchParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { DataCard, CardHeader, Spinner, useStatusMessage } from '../../components';
import { useRootCrumbNavigate } from '../item/RootCrumb';
import { loadAllTripsFresh } from '../../utilities/TripSchedule.mjs';
import { extractErrorText } from '../../fleet-shared/Common.mjs';
import { useIsMdUp } from '../../utilities/useIsMdUp';
import { urlForTripGroup } from '../../fleet-shared/Urls.mjs';
import {
  groupTrips,
  sortTripGroups,
  groupWithStoredLegs,
  localDayIndex,
} from '../../fleet-shared/TripGroups.mjs';
import { tripWithResolvedEndpoints } from '../../fleet-shared/TripRoute.mjs';
import {
  buildTripCalendar,
  initialCalendarMonth,
  stepCalendarMonth,
  calendarMonthParam,
  parseCalendarMonthParam,
  groupRunsOnOrAfter,
} from '../../fleet-shared/TripCalendar.mjs';
import { useCalendarMapLegs } from './useCalendarMapLegs';
import { calendarColorToken } from './tripCalendarColors.mjs';
import CalendarMonth from './CalendarMonth';
import CalendarAgenda from './CalendarAgenda';
import TripPicker, { TripLegend } from './TripPicker';
import './TripsCalendar.css';

// Lazy: the only import path on the trips page that pulls in maplibre-gl (~250
// KB gz). The anyOnMap gate below keeps it off the wire when there is no map.
const TripGroupMap = lazyWithReload(() => import('../../components/map/TripGroupMap'), 'TripGroupMap');

// A group key can hold anything a Trip Name can, commas included, so the list
// is built from individually-encoded keys rather than handed whole to
// URLSearchParams (which would only escape the separator by accident).
const encodeKeys = (keys) => [...keys].map(encodeURIComponent).join(',');

function decodeKeys(value) {
  if (value == null) return null; // absent -- meaning "the default"
  return value
    .split(',')
    .filter(Boolean)
    .map((part) => {
      try {
        return decodeURIComponent(part);
      } catch {
        return part; // a hand-mangled URL shouldn't blank the page
      }
    });
}

// `item` and `placeIndex` both come from the host page, which is already
// loading them for the breadcrumbs and the list's own name resolution.
//
// `item` is only ever handed onward in router state, so a null one while that
// fetch is in flight is fine -- the same tolerance the list's row handover has.
// `placeIndex` is a prop rather than a second `usePlaceIndex()` so that the
// table and the grid resolve every name against ONE snapshot: the module cache
// behind the hook expires on a timer, and two independent mounts could
// straddle that boundary and spell the same place two ways on one page. (The
// network read was always shared -- `fetchAllPlaces` caches the promise -- so
// this saves an index rebuild, not a request.)
const TripsCalendarSection = ({ itemId, item, placeIndex }) => {
  const navigate = useRootCrumbNavigate();
  const isMdUp = useIsMdUp();

  // Its own status line, for errors only. It does NOT narrate loading: the
  // list card sits directly above saying "Loading..." for a read of the same
  // table, and a second identical line under it tells the reader nothing. The
  // calendar simply appears when its legs land.
  const [StatusMessage, { showError, hideStatus }] = useStatusMessage();

  // The raw stored legs, exactly as the database gave them. Every write path
  // (there is only one -- the handover on a bar click) reads from HERE, never
  // from the resolved display copies below, and never from the list's
  // paginated array.
  const [legs, setLegs] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  // Every leg the item has. The item itself is the host's job.
  useEffect(() => {
    let mounted = true;
    const leaving = new AbortController();

    async function load() {
      try {
        const loadedLegs = await loadAllTripsFresh(itemId, { signal: leaving.signal });
        if (!mounted) return;
        setLegs(loadedLegs);
        // Clears a previous item's failure when the route changes under us.
        hideStatus();
      } catch (err) {
        // Named, because the list above reads the same GSI and reports its own
        // failure inside its card: an unqualified message would put the very
        // same sentence on the page twice with nothing to tell the two apart.
        // It also answers the question the reader actually has when only this
        // read failed -- why there is a list but no calendar.
        if (mounted) {
          showError(new Error(`The calendar could not load these trips. ${extractErrorText(err)}`));
        }
      }
    }

    load();
    return () => { mounted = false; leaving.abort(); };
    // A different item is a different URL, so this runs once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  // Every trip the item has, newest first -- the picker's order, and (see
  // tripCalendarColors) the order colors are bound in.
  //
  // Grouped from DISPLAY copies, exactly as the trips list does: a group's
  // from/to are a save-time mirror of its legs' routes, so a place renamed in
  // /me/places is only reachable through the waypoint's placeId.
  const allGroups = useMemo(() => {
    if (!legs) return null;
    const displayLegs = legs.map((leg) => tripWithResolvedEndpoints(leg, placeIndex));
    return sortTripGroups(groupTrips(displayLegs, { placeIndex }), 'desc');
  }, [legs, placeIndex]);

  const colorIndexByKey = useMemo(() => {
    const map = new Map();
    (allGroups || []).forEach((group, i) => map.set(group.key, i));
    return map;
  }, [allGroups]);
  const colorIndexFor = useCallback((key) => colorIndexByKey.get(key) ?? 0, [colorIndexByKey]);

  // Today, on the calendar's day axis. Recomputed per render rather than
  // memoized: it's two arithmetic ops, and pinning it would leave a tab left
  // open overnight highlighting yesterday.
  const todayIndex = localDayIndex(new Date());

  // What the picker has selected. The URL is the source of truth; its absence
  // means "whatever is still running", which is answered here rather than
  // written into the URL so a fresh visit tomorrow gets tomorrow's answer.
  const selectedKeys = useMemo(() => {
    if (!allGroups) return new Set();
    const fromUrl = decodeKeys(searchParams.get('trips'));
    if (fromUrl) {
      // A key naming a trip that has since been renamed or deleted is simply
      // dropped -- a stale link should still show what it can.
      const known = new Set(allGroups.map((g) => g.key));
      return new Set(fromUrl.filter((key) => known.has(key)));
    }
    return new Set(
      allGroups.filter((g) => groupRunsOnOrAfter(g, todayIndex)).map((g) => g.key)
    );
  }, [allGroups, searchParams, todayIndex]);

  const selectedGroups = useMemo(
    () => (allGroups || []).filter((g) => selectedKeys.has(g.key)),
    [allGroups, selectedKeys]
  );

  // The displayed month. Same rule: the URL wins, and its absence is answered
  // from the data rather than written back.
  const month = useMemo(() => {
    const fromUrl = parseCalendarMonthParam(searchParams.get('month'));
    if (fromUrl) return fromUrl;
    return initialCalendarMonth(selectedGroups, { now: new Date() });
  }, [searchParams, selectedGroups]);

  const calendar = useMemo(
    () => buildTripCalendar(selectedGroups, { ...month, today: new Date() }),
    [selectedGroups, month]
  );

  // `replace`, always -- and more emphatically now that these params sit on the
  // trips list's URL: Back should leave the trips page, not walk the user back
  // through every month they paged past on the way down it.
  const updateParams = useCallback(
    (changes) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value == null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const goToMonth = useCallback(
    (delta) => {
      const next = stepCalendarMonth(month.year, month.month, delta);
      updateParams({ month: calendarMonthParam(next.year, next.month) });
    },
    [month, updateParams]
  );

  const goToToday = useCallback(() => {
    const now = new Date();
    updateParams({ month: calendarMonthParam(now.getFullYear(), now.getMonth()) });
  }, [updateParams]);

  // Writing a selection pins the month too, unless the URL already names one.
  //
  // The default month is derived from the SELECTED trips, so without this,
  // taking the last visible trip off the calendar would recompute the default
  // against an empty selection and silently jump the heading to today's month
  // -- while the user was looking at June, having touched nothing but a
  // checkbox. Pinning freezes what they were already looking at.
  const setSelection = useCallback(
    (keys) =>
      updateParams({
        trips: encodeKeys(keys),
        ...(searchParams.get('month')
          ? {}
          : { month: calendarMonthParam(month.year, month.month) }),
      }),
    [updateParams, searchParams, month]
  );

  const toggleTrip = useCallback(
    (key, checked) => {
      const next = new Set(selectedKeys);
      if (checked) next.add(key);
      else next.delete(key);
      setSelection(next);
    },
    [selectedKeys, setSelection]
  );

  // Open the trip the bar belongs to.
  //
  // groupWithStoredLegs is not optional: the group was built from display
  // copies, and TripGroup writes from `location.state.group.legs` -- handing
  // over a resolved leg would put a name the gazetteer supplied into the next
  // save. (Same swap the trips list makes, out of its own array.)
  const onSelectGroup = useCallback(
    (group) => {
      navigate(urlForTripGroup(itemId, group.key), {
        state: { item, group: groupWithStoredLegs(group, legs || []) },
      });
    },
    [navigate, itemId, item, legs]
  );

  // The map's legs, and what it had to leave out.
  const { mapLegs, anyOnMap, hiddenGroupCount, noRouteGroupCount } =
    useCalendarMapLegs(selectedGroups);

  // A line or a marker opens the TRIP, exactly as its bar does -- every other
  // target here (bar, legend chip, agenda row) goes to the group, and routing
  // the map through onSelectGroup means it inherits the mandatory
  // groupWithStoredLegs swap rather than needing a second write path.
  const groupByKey = useMemo(
    () => new Map(selectedGroups.map((group) => [group.key, group])),
    [selectedGroups]
  );
  const onSelectMapLeg = useCallback(
    (leg) => {
      const group = groupByKey.get(leg.groupKey);
      if (group) onSelectGroup(group);
    },
    [groupByKey, onSelectGroup]
  );

  // Stable identities: TripGroupMap memoizes its marker set on chainKeyFor, so
  // an inline arrow would recompute it on every render of this page.
  const chainKeyFor = useCallback((leg) => leg.groupKey, []);
  const colorFor = useCallback(
    (leg) => calendarColorToken(colorIndexFor(leg.groupKey)),
    [colorIndexFor]
  );

  const hasTrips = Boolean(allGroups && allGroups.length);

  // No trips at all: render nothing but the status line. The list above already
  // says "No trips yet. Add one to plan your next outing." -- an empty calendar
  // card under it would say the same thing twice, and the old page's "Go to
  // trips" button would now point at the page it is on.
  return (
    <>
      <StatusMessage />

      {hasTrips && (
        <DataCard
          header={
            <CardHeader
              // aria-live so paging months is announced -- the heading is the
              // only thing that changes, and it changes without a navigation.
              text={<span aria-live="polite">{calendar.monthLabel}</span>}
              extraActions={
                <>
                  <Button variant="outline-secondary" onClick={() => goToMonth(-1)} aria-label="Previous month">
                    <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
                  </Button>
                  <Button variant="outline-secondary" onClick={goToToday}>Today</Button>
                  <Button variant="outline-secondary" onClick={() => goToMonth(1)} aria-label="Next month">
                    <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
                  </Button>
                  <Button variant="outline-primary" onClick={() => setShowPicker(true)}>
                    Show trips ({selectedKeys.size})
                  </Button>
                </>
              }
            />
          }
          body={
            <div>
              <div className="mb-3">
                <TripLegend
                  groups={selectedGroups}
                  colorIndexFor={colorIndexFor}
                  onRemove={(key) => toggleTrip(key, false)}
                />
              </div>

              {selectedGroups.length === 0 ? (
                <p className="text-center text-body-secondary mb-0">
                  No trips selected. Use <strong>Show trips</strong> to pick some.
                </p>
              ) : isMdUp ? (
                <CalendarMonth
                  calendar={calendar}
                  colorIndexFor={colorIndexFor}
                  placeIndex={placeIndex}
                  onSelectGroup={onSelectGroup}
                />
              ) : (
                <CalendarAgenda
                  calendar={calendar}
                  colorIndexFor={colorIndexFor}
                  placeIndex={placeIndex}
                  onSelectGroup={onSelectGroup}
                />
              )}

              {/* Said rather than silently dropped: a checked trip that isn't
                  in this month is the commonest reason the grid looks empty. */}
              {calendar.hidden.length > 0 && (
                <p className="text-center text-body-secondary small mt-3 mb-0">
                  {calendar.hidden.length === 1
                    ? '1 selected trip is not in this month.'
                    : `${calendar.hidden.length} selected trips are not in this month.`}
                </p>
              )}
            </div>
          }
        />
      )}

      {/* Its own card (TripGroupMap brings one), below the calendar rather
          than inside it: the grid is the page and the map is the second
          reading of it. The gate is outside the Suspense on purpose --
          rendering the Suspense anyway would download maplibre for a map with
          nothing on it, and the trips list has no other maplibre import. */}
      {anyOnMap && (
        <Suspense fallback={<Spinner />}>
          <TripGroupMap
            legs={mapLegs}
            onSelectLeg={onSelectMapLeg}
            title="Route"
            showWeather={false}
            chainKeyFor={chainKeyFor}
            colorFor={colorFor}
          />
        </Suspense>
      )}

      {/* Said rather than silently dropped, the same habit as the "not in this
          month" line above. Only ever shown beside the map: with nothing
          drawable there is no card, and a lone paragraph about an absent map
          explains less than the silence does. */}
      {anyOnMap && (hiddenGroupCount > 0 || noRouteGroupCount > 0) && (
        <p className="text-center text-body-secondary small mb-3">
          {[
            hiddenGroupCount > 0 &&
              (hiddenGroupCount === 1
                ? '1 older trip is not on the map.'
                : `${hiddenGroupCount} older trips are not on the map.`),
            noRouteGroupCount > 0 &&
              (noRouteGroupCount === 1
                ? '1 selected trip has no route.'
                : `${noRouteGroupCount} selected trips have no route.`),
          ]
            .filter(Boolean)
            .join(' ')}
        </p>
      )}

      <TripPicker
        show={showPicker}
        onHide={() => setShowPicker(false)}
        groups={allGroups || []}
        selectedKeys={selectedKeys}
        colorIndexFor={colorIndexFor}
        placeIndex={placeIndex}
        onToggle={toggleTrip}
        onSelectAll={() => setSelection((allGroups || []).map((g) => g.key))}
        onSelectNone={() => setSelection([])}
        onSelectRunning={() =>
          setSelection((allGroups || []).filter((g) => groupRunsOnOrAfter(g, todayIndex)).map((g) => g.key))
        }
      />
    </>
  );
};

export default TripsCalendarSection;
